import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, ref } from 'vue';
import appConfig from '@/appConfig';
import {
  SSearchResultDialog, SBookingRequestError, STimeConverter, SConstants,
  SRideTypeEnum, STripTypeEnum, SDateTypeEnum,
} from '@slg/web-customer-shared';
import DirectionSelection from './directionSelection/DirectionSelection.vue';
import RideTypeSelection from './rideTypeSelection/RideTypeSelection.vue';
import TripTypeSelection from './tripTypeSelection/TripTypeSelection.vue';
import ComfortSelection from './comfortSelection/ComfortSelection.vue';
import DatesSelection from './datesSelection/DatesSelection.vue';
import PassengersSelection from './passengersSelection/PassengersSelection.vue';
import FeaturesSelection from './featuresSelection/FeaturesSelection.vue';
import {
  useStore, useRouter, useVm, useGlobalErrorDialog, useGtm,
} from '@/composables';


const __default__ = {
  name: 'BookingForm',
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const store = useStore();
const router = useRouter();
const vm = useVm();
const { setGlobalError } = useGlobalErrorDialog();
const { trackGtmEvent } = useGtm();

const loading = ref(false);
const showSearchResultDialog = ref(false);
const showSearchResultDialogLoading = ref(false);
const bookingRequestErrorCode = ref<string | null>(null);
const openedPanels = ref<number[]>([]);

const getAdults = computed(() => store.getters['booking/getAdults']);
const getChildren = computed(() => store.getters['booking/getChildren']);
const getOutwardDate = computed(() => store.getters['booking/getOutwardDate']);
const getOutwardTime = computed(() => store.getters['booking/getOutwardTime']);
const getOutwardType = computed(() => store.getters['booking/getOutwardType']);
const getRideType = computed(() => store.getters['booking/getRideType']);
const getTripType = computed(() => store.getters['booking/getTripType']);
const getReturnDate = computed(() => store.getters['booking/getReturnDate']);
const getReturnTime = computed(() => store.getters['booking/getReturnTime']);
const getReturnType = computed(() => store.getters['booking/getReturnType']);
const getAnimalsTransportation = computed(() => store.getters['booking/getAnimalsTransportation']);
const getStops = computed(() => store.getters['booking/getStops']);
const getSelectedRides = computed(() => store.getters['booking/getSelectedRides']);
const getBookingRequestId = computed(() => store.getters['booking/getBookingRequestId']);
const getVehicleCategories = computed(() => store.getters['opsZone/getVehicleCategories']);
const getTimeZone = computed(() => store.getters['opsZone/getTimeZone']);
const getVehicleCategoryId = computed(() => store.getters['booking/getVehicleCategoryId']);
const atLeastOnePassengerSelected = computed(() => store.getters['booking/atLeastOnePassengerSelected']);
const getSelectedFeatures = computed(() => store.getters['booking/getSelectedFeatures']);
const isOpsZoneSelected = computed(() => store.getters['opsZone/isOpsZoneSelected']);
const getRejectReasonCode = computed(() => store.getters['booking/getRejectReasonCode']);

const isFeatureSelectionAvailable = computed(() => getSelectedFeatures.value?.length > 0 || !appConfig.isNightCardsSupport);
const amountPassengersError = computed(() => {
  const passengers = (vm.$refs as any)?.passengers;
  const shouldReadError = atLeastOnePassengerSelected.value || !atLeastOnePassengerSelected.value;
  return shouldReadError && passengers ? passengers.errors.counter[0] : '';
});
const getWheelchairPlacesAmountOrdered = computed(() => {
  const wheelChairFeature = getSelectedFeatures.value?.find((feature: any) => feature.vehicleFeatureId === SConstants.WHEELCHAIR_PLACE_ID);
  return wheelChairFeature?.amount;
});
const maxStandardSeats = computed(() => {
  const selectedVehicleCategory = getVehicleCategories.value
    ?.find((vehicleCategory: any) => vehicleCategory.vehicleCategoryId === getVehicleCategoryId.value);
  return selectedVehicleCategory?.maxStandardSeatCapacity + getWheelchairPlacesAmountOrdered.value;
});
const isMaxSeatsAreReached = computed(() => getAdults.value + getChildren.value === maxStandardSeats.value);
const componentWithError = computed(() => {
  const firstFailedObserver = (vm.$refs as any).bookingForm.observers.find((observer: any) => observer.flags.invalid);
  return (vm.$refs as any)[firstFailedObserver.$attrs.name];
});

function onCloseSearchResultDialog() {
  showSearchResultDialog.value = false;
  trackGtmEvent('Close requested trip pop-up button click', 'Button', 'Click', 'Close requested trip pop-up');
}

function toCheckoutPage() {
  trackGtmEvent('Confirm requested trip button click', 'Button', 'Click', 'Confirm requested trip');
  router.push({ name: 'Checkout' });
}

function buildSaveModel() {
  const model: Record<string, any> = {};
  model.adultsCount = getAdults.value;
  model.animalsTransportation = getAnimalsTransportation.value;
  model.arrivalTimeDeviation = null; //
  model.infantsCount = getChildren.value;
  model.opsZoneId = isOpsZoneSelected.value;
  model.outwardDateTime = STimeConverter.convertDateAndTimeToTimeZone(getOutwardDate.value, getOutwardTime.value, getTimeZone.value);
  model.outwardDeparture = getOutwardType.value === SDateTypeEnum.DEPARTURE_TIME;
  model.partnerId = null;
  model.privateShuttle = getRideType.value === SRideTypeEnum.PRIVATE;
  model.returnDateTime = getTripType.value === STripTypeEnum.ROUND_TRIP
    ? STimeConverter.convertDateAndTimeToTimeZone(getReturnDate.value, getReturnTime.value, getTimeZone.value)
    : null;
  model.returnDeparture = getReturnType.value === SDateTypeEnum.DEPARTURE_TIME;
  model.roundTrip = getTripType.value === STripTypeEnum.ROUND_TRIP;
  model.selectedFeatures = getSelectedFeatures.value.filter((feature: any) => feature.vehicleFeatureId !== SConstants.WHEELCHAIR_PLACE_ID);
  model.selectedFeatures = model.selectedFeatures.reduce((results: any[], feature: any) => {
    if (feature.amount) {
      results.push({
        capacity: feature.amount,
        vehicleFeatureId: feature.vehicleFeatureId,
      });
    }
    return results;
  }, []);
  model.specialSeatsCount = getWheelchairPlacesAmountOrdered.value;
  model.stops = getStops.value.map((stop: any) => ({
    address: stop.address,
    latitude: stop.lat,
    longitude: stop.lon,
    poiStopId: stop.poiStopId,
  }));
  model.vehicleCategoryId = getVehicleCategoryId.value;
  return model;
}

function expandSections() {
  const passengerSelectionError = (vm.$refs as any).bookingForm.observers
    .find((observer: any) => observer.flags.invalid && observer.$attrs.name === 'passengersSelection');
  const featuresSelectionError = (vm.$refs as any).bookingForm.observers
    .find((observer: any) => observer.flags.invalid && observer.$attrs.name === 'featuresSelection');

  if (passengerSelectionError && featuresSelectionError) {
    openedPanels.value = [0, 1];
  } else if (passengerSelectionError && !featuresSelectionError) {
    openedPanels.value = [0];
  } else if (!passengerSelectionError && featuresSelectionError) {
    openedPanels.value = [1];
  }
}

function onConfirmBookingRequest() {
  trackGtmEvent('Confirm requested trip button click', 'Button', 'Click', 'Confirm requested trip');
}

async function onSubmit() {
  trackGtmEvent('Get a quote button click', 'Button', 'Click', 'Get a quote');
  (vm.$refs as any).bookingForm.validate().then(async (success: boolean) => {
    if (!success) {
      expandSections();
      setTimeout(() => {
        componentWithError.value.$el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 100);
      return;
    }
    nextTick(async () => {
      const model = buildSaveModel();
      showSearchResultDialogLoading.value = true;
      trackGtmEvent('Booking form details set', 'Pop Up', 'Open', 'Confirmation pop-up is shown');
      try {
        showSearchResultDialog.value = true;
        await store.dispatch('booking/setBookingDetails', model);
        if (getRejectReasonCode.value) {
          bookingRequestErrorCode.value = getRejectReasonCode.value;
          showSearchResultDialog.value = false;
        }
      } catch (e) {
        showSearchResultDialog.value = false;
        await setGlobalError(e as any);
      }
      showSearchResultDialogLoading.value = false;
    });
  });
}

return { __sfc: true,store, router, vm, setGlobalError, trackGtmEvent, loading, showSearchResultDialog, showSearchResultDialogLoading, bookingRequestErrorCode, openedPanels, getAdults, getChildren, getOutwardDate, getOutwardTime, getOutwardType, getRideType, getTripType, getReturnDate, getReturnTime, getReturnType, getAnimalsTransportation, getStops, getSelectedRides, getBookingRequestId, getVehicleCategories, getTimeZone, getVehicleCategoryId, atLeastOnePassengerSelected, getSelectedFeatures, isOpsZoneSelected, getRejectReasonCode, isFeatureSelectionAvailable, amountPassengersError, getWheelchairPlacesAmountOrdered, maxStandardSeats, isMaxSeatsAreReached, componentWithError, onCloseSearchResultDialog, toCheckoutPage, buildSaveModel, expandSections, onConfirmBookingRequest, onSubmit, SSearchResultDialog, SBookingRequestError, DirectionSelection, RideTypeSelection, TripTypeSelection, ComfortSelection, DatesSelection, PassengersSelection, FeaturesSelection }
}

})